import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-white pt-20">
      {/* Header */}
      <section className="py-16 md:py-24 text-center px-6">
        <div className="max-w-[680px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-semibold text-[#1d1d1f] tracking-tight mb-4">
              Contact.
            </h1>
            <p className="text-base text-[#86868b]">
              Have questions? We're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-[980px] mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="space-y-8">
              <a 
                href="tel:0760784050"
                className="flex items-start gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-10 h-10 rounded-full bg-[#f5f5f7] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C41E3A]/10 transition-colors">
                  <Phone className="w-4 h-4 text-[#C41E3A]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868b] mb-0.5">Phone</p>
                  <p className="text-base font-medium text-[#1d1d1f] group-hover:text-[#C41E3A] transition-colors">
                    076 078 4050
                  </p>
                </div>
              </a>

              <a 
                href="mailto:info@maafricadetailing.co.za"
                className="flex items-start gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-10 h-10 rounded-full bg-[#f5f5f7] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C41E3A]/10 transition-colors">
                  <Mail className="w-4 h-4 text-[#C41E3A]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868b] mb-0.5">Email</p>
                  <p className="text-base font-medium text-[#1d1d1f] group-hover:text-[#C41E3A] transition-colors">
                    info@maafricadetailing.co.za
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#f5f5f7] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-[#C41E3A]" />
                </div>
                <div>
                  <p className="text-xs text-[#86868b] mb-0.5">Location</p>
                  <p className="text-base font-medium text-[#1d1d1f]">
                    68 Serenade Road
                  </p>
                  <p className="text-sm text-[#424245]">
                    Henville, Germiston
                  </p>
                  <p className="text-xs text-[#86868b] mt-1">
                    Serving Gauteng & surrounds
                  </p>
                </div>
              </div>
            </div>

            {/* Founder Quote */}
            <div className="mt-12 p-6 bg-[#f5f5f7] rounded-2xl">
              <p className="text-sm text-[#424245] italic leading-relaxed mb-4">
                "Your luxury car is more than transportation – it's a statement. Let us help you maintain that statement."
              </p>
              <p className="text-xs text-[#D4AF37] font-medium">
                — Bongane Goodenough Radebe, Founder
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs text-[#86868b] mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder="Your name"
                  className="input-apple"
                  data-testid="contact-form-name"
                />
              </div>

              <div>
                <label className="block text-xs text-[#86868b] mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  placeholder="your@email.com"
                  className="input-apple"
                  data-testid="contact-form-email"
                />
              </div>

              <div>
                <label className="block text-xs text-[#86868b] mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  placeholder="076 123 4567"
                  className="input-apple"
                  data-testid="contact-form-phone"
                />
              </div>

              <div>
                <label className="block text-xs text-[#86868b] mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  required
                  rows={4}
                  placeholder="How can we help?"
                  className="input-apple resize-none"
                  data-testid="contact-form-message"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full apple-btn apple-btn-primary btn-press disabled:opacity-50"
                data-testid="contact-submit"
              >
                {submitting ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </main>
  );
}
